/**
 * Unified locale modal: open/close, focus trap, Escape, overlay, dual POST (localization + currency).
 */
(function () {
  function qsa(el, sel) {
    return Array.prototype.slice.call(el.querySelectorAll(sel));
  }

  function focusablesIn(container) {
    var sel =
      'button:not([disabled]), [href], input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    return qsa(container, sel).filter(function (node) {
      try {
        if (node.closest && node.closest('[hidden],[aria-hidden="true"]')) return false;
      } catch (_ignored) {}
      return true;
    });
  }

  function setBusy(modalEl, visible) {
    var busy = modalEl.querySelector('[data-locale-spinner]');
    if (!busy) return;
    if (visible) busy.removeAttribute('hidden');
    else busy.setAttribute('hidden', '');
    busy.setAttribute('aria-hidden', visible ? 'false' : 'true');
  }

  function postSameOrigin(action, body, opts) {
    opts = opts || {};
    var controller = opts.signal ? null : new AbortController();
    var signal = opts.signal || (controller ? controller.signal : null);
    var ms = typeof opts.timeoutMs === 'number' ? opts.timeoutMs : 45000;
    var timer =
      controller && ms > 0
        ? window.setTimeout(function () {
            try {
              controller.abort();
            } catch (_e) {}
          }, ms)
        : null;

    return fetch(action, {
      method: 'POST',
      body: body,
      credentials: 'same-origin',
      redirect: 'follow',
      signal: signal,
      headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: '*/*' },
    }).finally(function () {
      if (timer) window.clearTimeout(timer);
    });
  }

  function clearLocaleModalTopInset(modalEl) {
    if (!modalEl || !modalEl.style) return;
    modalEl.style.removeProperty('--locale-modal-inset-top');
  }

  function setLocaleModalTopInset(modalEl) {
    if (!modalEl || !modalEl.style) return;
    var pad = 12;
    var h = 0;
    try {
      var sec = document.querySelector('.announcement-bar-section');
      if (sec) {
        var cs = window.getComputedStyle(sec);
        var op = parseFloat(cs.opacity);
        if (cs.display !== 'none' && cs.visibility !== 'hidden' && !isNaN(op) && op > 0.01) {
          var r = sec.getBoundingClientRect();
          if (r.height >= 8) h = Math.ceil(r.height);
        }
      }
    } catch (_e) {}
    modalEl.style.setProperty('--locale-modal-inset-top', h + pad + 'px');
  }

  function closeModal(modalId, modal, panel, teardown) {
    modal.setAttribute('hidden', '');
    modal.setAttribute('aria-hidden', 'true');
    if (modalId) {
      qsa(document, '[aria-controls="' + modalId + '"]').forEach(function (el) {
        el.setAttribute('aria-expanded', 'false');
      });
    }
    document.documentElement.classList.remove('locale-modal--open');

    document.removeEventListener('keydown', teardown.onDocKey);

    setBusy(modal, false);

    try {
      if (teardown.syncLocaleInsetOnResize && typeof teardown.syncLocaleInsetOnResize === 'function') {
        window.removeEventListener('resize', teardown.syncLocaleInsetOnResize);
        teardown.syncLocaleInsetOnResize = null;
      }
    } catch (_e) {}

    clearLocaleModalTopInset(modal);

    try {
      var back = teardown.lastOpenTrigger;
      if (back && typeof back.focus === 'function') back.focus();
    } catch (_e) {}
  }

  function openModal(trigger, modal, panel, teardownHolder, modalId) {
    setBusy(modal, false);

    teardownHolder.lastOpenTrigger = trigger;

    modal.removeAttribute('hidden');
    modal.setAttribute('aria-hidden', 'false');

    if (modalId) {
      qsa(document, '[aria-controls="' + modalId + '"]').forEach(function (el) {
        el.setAttribute('aria-expanded', el === trigger ? 'true' : 'false');
      });
    }
    document.documentElement.classList.add('locale-modal--open');

    setLocaleModalTopInset(modal);
    teardownHolder.syncLocaleInsetOnResize = function () {
      setLocaleModalTopInset(modal);
    };
    window.addEventListener('resize', teardownHolder.syncLocaleInsetOnResize);

    teardownHolder.onDocKey = function (evt) {
      if (evt.key === 'Escape') {
        closeModal(modalId, modal, panel, teardownHolder);
        return;
      }

      if (evt.key !== 'Tab') return;

      var list = focusablesIn(panel);
      if (!list.length) return;

      var first = list[0];
      var last = list[list.length - 1];
      var active = document.activeElement;

      if (!modal.contains(active)) {
        evt.preventDefault();
        first.focus();
        return;
      }

      if (evt.shiftKey) {
        if (active === first) {
          evt.preventDefault();
          last.focus();
        }
      } else if (active === last) {
        evt.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', teardownHolder.onDocKey);

    window.setTimeout(function () {
      var focusSel = trigger.getAttribute && trigger.getAttribute('data-locale-open-focus');
      var target = null;
      if (focusSel === 'currency') {
        target = modal.querySelector('[data-locale-currency-select]');
        if (target && typeof target.focus === 'function') {
          try {
            target.focus();
            return;
          } catch (_ignored) {}
        }
      }
      if (focusSel === 'lang' || focusSel === 'language') {
        target = modal.querySelector('[data-locale-lang-select]');
        if (target && typeof target.focus === 'function') {
          try {
            target.focus();
            return;
          } catch (_ignored) {}
        }
      }
      var list = focusablesIn(panel);
      if (list[0]) list[0].focus();
    }, 10);
  }

  function applyDefaultCountryAndMarketMeta(modalRoot) {
    var sel = modalRoot.querySelector('[data-locale-country-select]');
    var meta = modalRoot.querySelector('[data-locale-market-currencies]');

    if (meta && sel) {
      var raw = (meta.textContent || '').replace(/^\s+|\s+$/g, '');
      if (raw && raw !== '[]') {
        try {
          var rows = JSON.parse(raw);
          var map = {};
          for (var i = 0; i < rows.length; i++) {
            var r = rows[i];
            map[r.c] = r;
          }
          Array.prototype.forEach.call(sel.options, function (opt) {
            var m = map[opt.value];
            if (!m) return;
            opt.setAttribute('data-currency-iso', m.i);
            opt.setAttribute('data-currency-symbol', m.s);
            opt.setAttribute('data-currency-name', m.n);
          });
        } catch (_ignored) {}
      }
    }

    if (!sel) return;
    var def = sel.getAttribute('data-default-country');
    if (!def) return;
    sel.value = def;
  }

  function bindDualCurrencySubmit(form, modal, trigger, panel, teardown) {
    if (form.getAttribute('data-locale-submit-bound') === '1') return;
    form.setAttribute('data-locale-submit-bound', '1');

    form.addEventListener('submit', function (evt) {
      var dual = form.getAttribute('data-locale-dual-currency') === 'true';
      if (!dual) return;

      evt.preventDefault();

      var currencyForm = document.getElementById('LocaleCartCurrencyForm');
      var currencySelect = form.querySelector('[data-locale-currency-select]');
      var cartInput = currencyForm ? currencyForm.querySelector('[data-locale-cart-currency-input]') : null;

      if (!currencyForm || !currencySelect || !cartInput) {
        form.submit();
        return;
      }

      var fdLoc = new FormData(form);
      fdLoc.delete('currency_code');

      var locAction = form.getAttribute('action') || '/localization';
      setBusy(modal, true);

      postSameOrigin(locAction, fdLoc)
        .then(function (res) {
          if (!res || !res.ok) throw new Error('locale_post');
          cartInput.value = currencySelect.value;
          var fdCur = new FormData(currencyForm);
          var curAction = currencyForm.getAttribute('action');
          return postSameOrigin(curAction, fdCur);
        })
        .then(function (res) {
          if (!res || !res.ok) throw new Error('currency_post');
          window.location.reload();
        })
        .catch(function () {
          setBusy(modal, false);
          try {
            form.submit();
          } catch (_e) {
            window.location.reload();
          }
        });
    });
  }

  function bindModal(modal, modalId) {
    var panel = modal.querySelector('.locale-modal__panel');
    var overlay = modal.querySelector('.locale-modal__overlay[data-locale-close]');
    if (!panel || !overlay || !modalId) return;

    var teardown = { onDocKey: function () {}, lastOpenTrigger: null, modalId: modalId };

    var triggers = qsa(document, '[aria-controls="' + modalId + '"]');
    if (!triggers.length) return;

    triggers.forEach(function (trigger) {
      trigger.addEventListener('click', function () {
        openModal(trigger, modal, panel, teardown, modalId);
      });
    });

    overlay.addEventListener('click', function (e) {
      if (e.target === overlay) closeModal(modalId, modal, panel, teardown);
    });

    qsa(modal, '[data-locale-close]').forEach(function (el) {
      if (el !== overlay && el.closest('.locale-modal__panel'))
        el.addEventListener('click', function () {
          closeModal(modalId, modal, panel, teardown);
        });
    });

    var primaryTrigger = triggers[0];

    var form = modal.querySelector('#LocaleForm');
    if (form) bindDualCurrencySubmit(form, modal, primaryTrigger, panel, teardown);

    applyDefaultCountryAndMarketMeta(modal);
  }

  function initLocaleModals(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var modals =
      typeof scope.querySelectorAll === 'function' ? scope.querySelectorAll('[data-locale-modal]') : document.querySelectorAll('[data-locale-modal]');

    Array.prototype.forEach.call(modals, function (modal) {
      var mid = modal.getAttribute('id');
      if (!mid || modal.getAttribute('data-locale-bound') === '1') return;

      bindModal(modal, mid);
      modal.setAttribute('data-locale-bound', '1');
    });
  }

  function onReady() {
    initLocaleModals(document);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', onReady);
  else onReady();

  document.addEventListener('shopify:section:load', function (evt) {
    if (evt && evt.target) initLocaleModals(evt.target);
    else initLocaleModals(document);
  });
})();
